import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntity } from 'app/entities/truck-implement/truck-implement.reducer';
import { getEntity as getFenceCircleAlert } from 'app/entities/virtual-fence-circle-alert/virtual-fence-circle-alert.reducer';
import {
  getStorageOptions,
  IdStorageTypes,
  setStorageOptions,
  ValuesStorageTypes,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/storageOptions';
import { IPosition } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { getAddress } from 'app/shared/reducers/geocode.reducer';
import { convertDateTimeToPtBRWithYear } from 'app/shared/util/date-utils';
import { isEmptyObject } from 'app/shared/util/functions-util';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import Circle from 'react-google-maps/lib/components/Circle';
import Polygon from 'react-google-maps/lib/components/Polygon';
import StreetViewPanorama from 'react-google-maps/lib/components/StreetViewPanorama';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';

const VirtualFenceAlertMap = ({ props }: RouteComponentProps<{ id: string; virtualFenceAlertId: string }> | any) => {
  const dispatch = useAppDispatch();
  const [zoom, setZoom] = useState<number>(4);
  const [latVirtualFence] = useState(-23.486707);
  const [lngVirtualFence] = useState(-46.515203);
  const [center] = useState<IPosition>({ lat: latVirtualFence, lng: lngVirtualFence });
  const [mapType, setMapType] = useState<ValuesStorageTypes>(getStorageOptions(IdStorageTypes.MAP_TYPE) ?? 'MAP');
  const [truckImplementId, setTruckImplementId] = useState<number | null>(null);
  const [onActiveMarker, setOnActiveMarker] = useState<boolean>(false);
  const [alertAddress, setAlertAddress] = useState<string>('');
  const loading = useAppSelector(state => state.virtualFenceCircleAlert.loading);
  const mapRef = useRef(null);
  const virtualFenceCircleAlert = useAppSelector(state => state.virtualFenceCircleAlert.entity);
  const truckImplement = useAppSelector(state => state.truckImplement.entity);
  const [loadingAddress, setLoadingAddress] = useState<boolean>(true);

  const lastPositionTruckImplementIcon = {
    url: 'content/images/map-icons/icon_pin_end.png',
    scaledSize: new google.maps.Size(40, 40),
  };

  useEffect(() => {
    dispatch(getEntity(props.match.params.id));
    dispatch(getFenceCircleAlert(props.match.params.virtualFenceAlertId));
  }, []);

  function onClickMapType() {
    document.getElementsByClassName('gm-style-mtc')[0].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1].addEventListener('click', () => {
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  }

  useEffect(() => {
    setFitBounds();
  }, [virtualFenceCircleAlert]);

  const setFitBounds = () => {
    if (!virtualFenceCircleAlert?.vehiclePosition || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    const vp = virtualFenceCircleAlert.vehiclePosition;

    bounds.extend({ lat: vp.latitude, lng: vp.longitude });

    const isPolygon = virtualFenceCircleAlert.fenceType === 'POLYGON';

    if (!isPolygon && virtualFenceCircleAlert.virtualFenceCircle) {
      const fence = virtualFenceCircleAlert.virtualFenceCircle;
      const delta = ((fence.radius ?? 500) / 500) * 0.0089;
      bounds.extend({ lat: fence.latitude - delta, lng: fence.longitude - delta });
      bounds.extend({ lat: fence.latitude + delta, lng: fence.longitude + delta });
    } else if (isPolygon && virtualFenceCircleAlert.virtualFencePolygon?.points?.length) {
      virtualFenceCircleAlert.virtualFencePolygon.points.forEach(pt => {
        if (pt.latitude != null && pt.longitude != null) {
          bounds.extend({ lat: pt.latitude, lng: pt.longitude });
        }
      });
    }

    mapRef.current.fitBounds(bounds);
  };

  const getAlertAddress = (lat: number, lon: number) => {
    getAddress(lat, lon)
      .then(resp => {
        setAlertAddress(resp.data.address);
      })
      .then(() => setLoadingAddress(false));
  };

  function renderRedirect() {
    props.history.push('/dashboards');
    return <Redirect to={`truck-implement/${truckImplementId}`} />;
  }

  const renderLoading = useCallback(
    () =>
      loading && (
        <div className="spinner-map">
          <Spinner color='#000814'/>
        </div>
      ),
    [loading]
  );

  const renderCircle = () => {
    if (virtualFenceCircleAlert.fenceType === 'POLYGON' || !virtualFenceCircleAlert.virtualFenceCircle) {
      return null;
    }
    const fence = virtualFenceCircleAlert.virtualFenceCircle;
    return (
      <Circle
        center={{
          lat: fence.latitude,
          lng: fence.longitude,
        }}
        radius={fence.radius}
        options={{
          fillColor: fence.color,
          fillOpacity: 0.5,
          zIndex: 1,
          strokeWeight: 0.3,
          strokeColor: fence.color,
        }}
      />
    );
  };

  const renderPolygonFence = () => {
    const polygon = virtualFenceCircleAlert.virtualFencePolygon;
    if (virtualFenceCircleAlert.fenceType !== 'POLYGON' || !polygon?.points?.length) {
      return null;
    }
    const paths = polygon.points
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(pt => ({ lat: pt.latitude, lng: pt.longitude }));
    return (
      <Polygon
        paths={paths}
        options={{
          fillColor: polygon.color ?? '#dc3545',
          fillOpacity: 0.5,
          strokeColor: polygon.color ?? '#dc3545',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          zIndex: 1,
        }}
      />
    );
  };

  const renderMarker = () => {
    const item = virtualFenceCircleAlert.vehiclePosition;
    return (
      <Marker
        icon={lastPositionTruckImplementIcon}
        position={{ lat: item.latitude, lng: item.longitude }}
        onClick={() => {
          setOnActiveMarker(prevOnActive => !prevOnActive);
          getAlertAddress(item.latitude, item.longitude);
        }}
        label={{ text: truckImplement?.licensePlate, className: 'label-icon' }}
      >
        {onActiveMarker && (
          <InfoWindow onCloseClick={() => setOnActiveMarker(prevOnActive => !prevOnActive)}>
            {!loadingAddress && truckImplement ? (
              <div className="container-company-implement-info" onClick={() => setTruckImplementId(truckImplement?.id)}>
                {truckImplement.image && <img src={`data:image/*;base64,${truckImplement.image}`} alt="imagem-empresa" />}
                <dl className="m-0 mt-2 mb-n1">
                  <dt>Placa:</dt>
                  <dd>{truckImplement.licensePlate}</dd>
                  {/* <dt>Motorista:</dt>
                  {infoDetail.fullName ? <dd>{infoDetail.fullName}</dd> : <dd>Nenhum motorista</dd>} */}
                  <dt>Posição registrada:</dt>
                  {alertAddress ? <dd className="m-0">{alertAddress}</dd> : <dd>Sem endereço</dd>}
                  <div>
                    <dd className="m-0">{convertDateTimeToPtBRWithYear(item.created)}</dd>
                    <FontAwesomeIcon icon="eye" title="Visualizar implemento" />
                  </div>
                </dl>
              </div>
            ) : (
              <div className="p-5">
                <Spinner color='info'/>
              </div>
            )}
          </InfoWindow>
        )}
      </Marker>
    );
  };

  return (
    <Fragment>
      {truckImplementId && renderRedirect()}
      <GoogleMap
        ref={mapRef}
        zoom={zoom}
        center={center}
        defaultOptions={{ styles: mapStyle }}
        onMapTypeIdChanged={onClickMapType}
        mapTypeId={google.maps.MapTypeId[mapType]}
        onClick={() => {
          setZoom(5);
        }}
      >
        <StreetViewPanorama visible={false} />
        {renderLoading()}
        {!loading && isEmptyObject(virtualFenceCircleAlert) && renderCircle()}
        {!loading && isEmptyObject(virtualFenceCircleAlert) && renderPolygonFence()}
        {!loading && isEmptyObject(virtualFenceCircleAlert) && renderMarker()}
      </GoogleMap>
    </Fragment>
  );
};

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

export default compose(
  withProps({
    googleMapURL: G_API_URL,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `72vh` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(VirtualFenceAlertMap);
